import React, { useState, useEffect } from "react";
import { Button, Collapse, Popconfirm, Tag } from "antd";
import axios from "axios";
import moment from "moment";
import { isAuthenticated } from "../../Auth/auth";
import { Error, Success } from "../../../commons/Messages/Messages";
import GeneralTable from "../../GeneralTable";
import AddSalarySlipModal from "./AddSalarySlip";
import DeleteModal from "../../DeleteModal/DeleteModal";
import DropdownComp from "../../Dropdowns/DropdownComp";
import UpdateSalarySlipModal from "./UpdateSalarySlip";
import { checkSpecificPermission } from "../../../utils/checkPermissions";
import { generalErrorAlert } from "../../GeneralErrorAlert";
import LohnsteuerCalculator from "../../Payroll/LohnsteuerCalculator";
import { formatCents } from "../../Payroll/payrollOptions";
import PageToolbar from "../../../design-system/PageToolbar";

import { useTranslation } from "react-i18next";
const SalarySlipsList = ({ employee }) => {
  const { t } = useTranslation("dashboard");
  const [salarySlips, setSalarySlips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState();

  const user = isAuthenticated();

  const fetchSalarySlips = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/salary-slips/get`,
        { employee: employee?._id, pageSize, pageNo: pageNo - 1 },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        }
      );
      setSalarySlips(res.data?.data);
      setTotalCount(res.data?.count);
    } catch (err) {
      console.error(err);
      generalErrorAlert(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSalarySlips();
  }, [employee, pageSize, pageNo]);

  const deleteHandler = async id => {
    await axios
      .delete(`${process.env.REACT_APP_BACKEND_URL}/salary-slips/delete/${id}`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token")
        }
      })
      .then(res => {
        if (res.status === 200 || res.status === 304) {
          Success(res.data.successMessage);
          fetchSalarySlips();
        } else {
          Error(res.data.errorMessage);
        }
      })
      .catch(err => {
        generalErrorAlert(err);
      });
  };

  const finaliseHandler = async id => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/salary-slips/finalise/${id}`,
        {},
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      Success(res.data.successMessage);
      fetchSalarySlips();
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const columns = [
    {
      title: "#",
      dataIndex: "_id",
      mobile: "hidden",
      render: (text, record, index) => index + 1
    },
    {
      title: t("Title"),
      dataIndex: "title",
      mobile: "title"
    },
    {
      title: t("Month"),
      dataIndex: "startDate",
      render: date => {
        return <div>{moment(date).format("MMMM YYYY")}</div>;
      }
    },
    {
      title: t("Work Hours"),
      dataIndex: "totalHoursWorked",
      render: text => `${text}`
    },
    {
      title: t("Gross"),
      dataIndex: "totalSalary",
      render: (text, obj) =>
        formatCents(obj?.payroll?.bruttoCents ?? Math.round(Number(text || 0) * 100))
    },
    {
      title: t("Leaves"),
      dataIndex: "totalUnpaidLeaves",
      mobile: "hidden"
    },
    {
      title: t("Lohnsteuer"),
      dataIndex: ["payroll", "lohnsteuerCents"],
      // Slips created before a Steuerklasse was on file carry no payroll block at all.
      render: cents => (cents === undefined ? "—" : formatCents(cents))
    },
    {
      title: t("Soli + Kirchensteuer"),
      key: "soliKirchensteuer",
      render: (text, obj) =>
        obj?.payroll
          ? formatCents(
              (obj.payroll.solidaritaetszuschlagCents || 0) + (obj.payroll.kirchensteuerCents || 0)
            )
          : "—"
    },
    {
      title: t("Social insurance"),
      dataIndex: ["payroll", "employeeContributions", "total"],
      render: cents => (cents === undefined ? "—" : formatCents(cents))
    },
    {
      title: t("Net pay"),
      dataIndex: "finalTotalSalary",
      render: (text, obj) =>
        formatCents(obj?.payroll?.nettoCents ?? Math.round(Number(text || 0) * 100))
    },
    {
      title: t("Status"),
      dataIndex: "locked",
      mobile: "subtitle",
      render: locked =>
        locked ? <Tag color="green">{t("Finalised")}</Tag> : <Tag color="orange">{t("Draft")}</Tag>
    },
    {
      title: t("Action"),
      key: "action",
      mobile: "actions",
      render: (text, obj) => {
        let menuItems = [
          {
            key: "view",
            link: `/salary/details/${obj?._id}`
          }
        ];
        // A finalised slip has to stay unchanged for the retention period, so editing and
        // deleting disappear once it is locked.
        if (
          !obj?.locked &&
          checkSpecificPermission(user?.roleId?.permissions, "Salary-Slips", "Update")
        ) {
          menuItems = [
            ...menuItems,
            {
              key: "edit",
              component: () => (
                <UpdateSalarySlipModal formData={obj} refreshParent={fetchSalarySlips} />
              )
            },
            {
              key: "finalise",
              component: () => (
                <Popconfirm
                  title={t("Finalise this salary slip?")}
                  description={t(
                    "Once finalised it can no longer be changed or deleted. Correct it by issuing a new slip."
                  )}
                  okText={t("Finalise")}
                  onConfirm={() => finaliseHandler(obj?._id)}
                >
                  <Button type="link">{t("Finalise")}</Button>
                </Popconfirm>
              )
            }
          ];
        }
        if (
          !obj?.locked &&
          checkSpecificPermission(user?.roleId?.permissions, "Salary-Slips", "Delete")
        ) {
          menuItems = [
            ...menuItems,
            {
              key: "delete",
              component: () => (
                <DeleteModal text="salary slip" onClick={() => deleteHandler(obj?._id)} />
              )
            }
          ];
        }
        return (
          <div>
            <DropdownComp menuItems={menuItems} />
          </div>
        );
      }
    }
  ];

  const refreshParent = data => {
    setSalarySlips([data, ...salarySlips]);
  };

  return (
    <div className="salary-slips">
      {checkSpecificPermission(user?.roleId?.permissions, "Salary-Slips", "Create") && (
        <PageToolbar
          actions={<AddSalarySlipModal employee={employee?._id} refreshParent={refreshParent} />}
        />
      )}
      <Collapse
        className="mb-3"
        items={[
          {
            key: "calculator",
            label: t("Lohnsteuer calculator — enter a gross salary and a Steuerklasse"),
            children: <LohnsteuerCalculator employee={employee} />
          }
        ]}
      />

      <GeneralTable
        loading={loading}
        pageNo={pageNo}
        pageSize={pageSize}
        totalCount={totalCount}
        data={salarySlips}
        columns={columns}
        setPageNo={setPageNo}
      />
    </div>
  );
};

export default SalarySlipsList;
